import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "@/lib/useNav";
import { formatDateShort } from "@/lib/format";
import { KPIProgressRow } from "@/components/kpi/KPIBits";
import { VoucherStatusPill, voucherStatus } from "@/components/modals/VoucherModal";
import { useBlockData, BlockEmpty } from "@/components/dashboard/useBlockData";
import { usePermissions } from "@/context/PermissionsContext";

export function KpisBlock() {
  const { can } = usePermissions();
  return can("kpis", "view") ? <KpisList /> : null;
}

function KpisList() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [kpis] = useBlockData(() => client.get("/kpis/progress", { params: { user_id: user.id } }).then((r) => r.data), [user.id]);
  if (!can("kpis", "view")) return null;
  if (!kpis) return null;
  if (!kpis.length) return <BlockEmpty>No KPIs assigned to you</BlockEmpty>;
  return (
    <div className="grid gap-x-8 gap-y-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
      {kpis.map((k) => (
        <div key={k.id}>
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-sm font-medium text-slate-700">{k.name}</span>
            <span className="text-[11px] text-slate-400">{k.period_label}</span>
          </div>
          {k.rows[0] && <KPIProgressRow row={k.rows[0]} />}
        </div>
      ))}
    </div>
  );
}

function ListRow({ onClick, testid, title, sub, right }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-50">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800">{title}</p>
        <p className="truncate text-xs text-slate-400">{sub}</p>
      </div>
      {right}
    </button>
  );
}

export function PrintOrdersBlock({ block }) {
  const s = block.settings || {};
  const navigate = useNavigate();
  const [rows] = useBlockData(() => client.get("/print-orders").then((r) => r.data), []);
  if (!rows) return null;
  const list = rows.filter((p) => !s.statuses?.length || s.statuses.includes(p.status)).slice(0, s.limit || 8);
  if (!list.length) return <BlockEmpty>No print orders</BlockEmpty>;
  return (
    <div className="space-y-0.5">
      {list.map((p) => (
        <ListRow key={p.id} onClick={() => navigate("/print-orders")} testid={`block-${block.id}-print-${p.id}`}
          title={`${p.item || "Print order"}${p.quantity ? ` × ${p.quantity}` : ""}`}
          sub={[p.property_name, p.date_ordered && formatDateShort(p.date_ordered)].filter(Boolean).join(" · ")}
          right={<span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{p.status}</span>} />
      ))}
    </div>
  );
}

export function VouchersBlock({ block }) {
  const s = block.settings || {};
  const navigate = useNavigate();
  const [rows] = useBlockData(() => client.get("/vouchers").then((r) => r.data), []);
  if (!rows) return null;
  const today = new Date().toLocaleDateString("en-CA");
  const in30 = new Date(Date.now() + 30 * 864e5).toLocaleDateString("en-CA");
  const list = (s.mode === "expiring"
    ? rows.filter((v) => voucherStatus(v) === "Active" && v.date_expiry && v.date_expiry >= today && v.date_expiry <= in30).sort((a, b) => a.date_expiry.localeCompare(b.date_expiry))
    : rows).slice(0, s.limit || 8);
  if (!list.length) return <BlockEmpty>{s.mode === "expiring" ? "Nothing expiring soon" : "No vouchers"}</BlockEmpty>;
  return (
    <div className="space-y-0.5">
      {list.map((v) => (
        <ListRow key={v.id} onClick={() => navigate("/prize-vouchers")} testid={`block-${block.id}-voucher-${v.id}`}
          title={`${v.voucher_no ? `#${v.voucher_no} · ` : ""}${v.gift || "Voucher"}`}
          sub={[v.property_name, v.issued_to, v.date_expiry && `Expires ${formatDateShort(v.date_expiry)}`].filter(Boolean).join(" · ")}
          right={<VoucherStatusPill status={voucherStatus(v)} />} />
      ))}
    </div>
  );
}
